import { useState } from 'react';

import { useMutation } from 'react-query';

import { makePerformSocialLoginUseCase } from 'modules/authentication/domain/useCases';
import { useAuth } from 'modules/authentication/presentation/contexts';
import { HTTPError } from 'shared/domain/errors';
import {
  TRoutePathsWithParams,
  useNavigation,
  useRouteQuery,
} from 'shared/presentation/contexts';
import { useRequestErrorHandler } from 'shared/presentation/hooks';

const isPhoneRequiredError = (error: unknown) =>
  error instanceof HTTPError && !!error.response?.phoneRequired;

export default function useSocialLogin() {
  const navigation = useNavigation();
  const { origin } = useRouteQuery<'/login'>();
  const auth = useAuth();
  const { handleError } = useRequestErrorHandler('authentication');

  const [phoneRequired, setPhoneRequired] = useState(false);

  const mutation = useMutation(
    (phone?: string) => makePerformSocialLoginUseCase().execute(phone),
    {
      onSuccess: session => {
        setPhoneRequired(false);
        auth.login(session);

        if (origin) navigation.push(decodeURI(origin) as TRoutePathsWithParams);
      },
      onError: error => {
        if (isPhoneRequiredError(error)) {
          setPhoneRequired(true);
          return;
        }

        handleError(error);
      },
    },
  );

  return {
    loading: mutation.isLoading,
    login: () => mutation.mutateAsync(undefined).catch(() => undefined),
    phoneRequired,
    closePhoneModal: () => setPhoneRequired(false),
    submitPhone: (phone: string) =>
      mutation.mutateAsync(phone).catch(() => undefined),
  };
}
